// The settings the shell owns: the person's (the account's picture and name
// where there is an account, language, time zone) and this device's (theme,
// send key). A host may add rows to Appearance (cloud: the home screen icon).
import { type Locale, m } from "@trameria/i18n";
import { type ComponentType, useMemo, useState } from "react";
import type { AppearanceRows } from "../app/context";
import type { SettingsItem } from "../app/screen-part";
import { browserTimeZone } from "../data/api";
import { apiErrorMessage } from "../data/errors";
import { useDeviceSetting } from "../device/device-setting";
import { sendKeySetting, themeSetting } from "../device/settings";
import { syncAccountLocale } from "../i18n/locale";
import { useAccount, useAccountSaves } from "../me/account";
import { useProfile, useUpdateProfile } from "../me/profile";
import { Avatar, Choices, ErrorLine, RowInput } from "../ui/bits";
import { iconOf } from "../ui/icon";
import { isImeComposing } from "../ui/ime";
import { SelectMenu } from "../ui/menu";
import { PictureField } from "../ui/picture-field";
import { SettingsBox, SettingsRow } from "./settings-page";

function General({ account }: { account: boolean }) {
  const profile = useProfile();
  const update = useUpdateProfile();
  const saved = profile.data;
  return (
    <SettingsBox>
      {account && <AccountRows />}
      <SettingsRow
        id="language"
        label={m.settings_language()}
        description={m.settings_language_note()}
      >
        <div className="flex flex-col items-end">
          <Choices<"auto" | Locale>
            label={m.settings_language()}
            value={saved?.locale ?? "auto"}
            disabled={!saved || update.isPending}
            options={[
              { value: "auto", label: m.settings_language_browser() },
              // i18n-ignore: a language's name is written in that language
              { value: "ja", label: "日本語" },
              { value: "en", label: "English" },
            ]}
            onChange={(v) => {
              const locale = v === "auto" ? null : v;
              update.mutate(
                { locale },
                { onSuccess: () => syncAccountLocale(locale) },
              );
            }}
          />
          {update.isError && update.variables?.locale !== undefined && (
            <ErrorLine>
              {apiErrorMessage(update.error, m.settings_save_failed())}
            </ErrorLine>
          )}
        </div>
      </SettingsRow>
      <SettingsRow
        id="time-zone"
        label={m.settings_time_zone()}
        description={m.settings_time_zone_note()}
      >
        <TimeZoneField />
      </SettingsRow>
    </SettingsBox>
  );
}

/** The account's picture and name, where there is one (me/account.ts). */
function AccountRows() {
  const account = useAccount();
  const saves = useAccountSaves();
  const seed = useProfile().data?.id;
  if (account === null) return null;
  return (
    <>
      <SettingsRow id="picture" label={m.settings_picture()}>
        <PictureField
          label={m.settings_picture()}
          preview={
            <Avatar
              name={account?.name || "?"}
              image={account?.image ?? null}
              seed={seed}
              size={28}
            />
          }
          removable={Boolean(account?.hasPicture)}
          {...saves.picture}
          onRemove={saves.removePicture}
        />
      </SettingsRow>
      <SettingsRow id="name" label={m.settings_name()}>
        <div className="w-full">
          {account && (
            <NameField
              key={account.name}
              saved={account.name}
              update={saves.name}
            />
          )}
        </div>
      </SettingsRow>
    </>
  );
}

function NameField({
  saved,
  update,
}: {
  saved: string;
  update: ReturnType<typeof useAccountSaves>["name"];
}) {
  const [name, setName] = useState(saved);
  const save = () => {
    const next = name.trim();
    if (!next) {
      setName(saved);
      return;
    }
    if (next !== saved) update.mutate(next);
  };
  return (
    <>
      <RowInput
        aria-label={m.settings_name()}
        value={name}
        maxLength={100}
        onChange={(e) => setName(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !isImeComposing(e.nativeEvent)) {
            e.preventDefault();
            save();
          }
        }}
      />
      {update.isError && (
        <ErrorLine>
          {apiErrorMessage(update.error, m.settings_save_failed())}
        </ErrorLine>
      )}
    </>
  );
}

function TimeZoneField() {
  const profile = useProfile();
  const update = useUpdateProfile();
  const saved = profile.data?.timeZone ?? null;
  const zones = useMemo(() => {
    const all = Intl.supportedValuesOf("timeZone");
    return saved !== null && !all.includes(saved) ? [saved, ...all] : all;
  }, [saved]);
  return (
    <div className="w-full">
      <SelectMenu
        label={m.settings_time_zone()}
        value={saved ?? ""}
        disabled={!profile.data}
        options={[
          {
            value: "",
            label: m.settings_time_zone_browser({ zone: browserTimeZone() }),
          },
          ...zones.map((z) => ({ value: z, label: z })),
        ]}
        onChange={(v) => update.mutate({ timeZone: v === "" ? null : v })}
      />
      {update.isError && update.variables?.timeZone !== undefined && (
        <ErrorLine>
          {apiErrorMessage(update.error, m.settings_save_failed())}
        </ErrorLine>
      )}
    </div>
  );
}

function Appearance({ Rows }: { Rows?: ComponentType }) {
  const [theme, setTheme] = useDeviceSetting(themeSetting);
  return (
    <SettingsBox>
      <SettingsRow
        id="theme"
        label={m.settings_theme()}
        description={m.settings_theme_note()}
      >
        <Choices
          label={m.settings_theme()}
          value={theme}
          onChange={setTheme}
          options={[
            { value: "auto", label: m.settings_theme_auto() },
            { value: "light", label: m.settings_theme_light() },
            { value: "dark", label: m.settings_theme_dark() },
          ]}
        />
      </SettingsRow>
      {Rows && <Rows />}
    </SettingsBox>
  );
}

function Input() {
  const [sendKey, setSendKey] = useDeviceSetting(sendKeySetting);
  return (
    <SettingsBox>
      <SettingsRow
        id="send-key"
        label={m.settings_send_key()}
        description={m.settings_send_key_note()}
      >
        <Choices
          label={m.settings_send_key()}
          value={sendKey}
          onChange={setSendKey}
          options={[
            { value: "mod", label: m.settings_send_key_mod() },
            { value: "enter", label: m.settings_send_key_enter() },
          ]}
        />
      </SettingsRow>
    </SettingsBox>
  );
}

/**
 * The shell's settings items. `appearanceRows` are the host's rows at the end
 * of Appearance's box (cloud: the home screen icon); with `account`, the
 * account's picture and name lead General (cloud, a Hub tied to it). The
 * offline edition on its own has neither.
 */
export const shellSettings = ({
  appearanceRows,
  account = false,
}: {
  appearanceRows?: AppearanceRows;
  account?: boolean;
} = {}): readonly SettingsItem[] => [
  {
    id: "general",
    order: 10,
    label: () => m.settings_general(),
    icon: iconOf("sliders"),
    description: () =>
      account
        ? m.settings_general_description_account()
        : m.settings_general_description(),
    sections: [
      ...(account
        ? [
            { id: "picture", label: () => m.settings_picture() },
            { id: "name", label: () => m.settings_name() },
          ]
        : []),
      { id: "language", label: () => m.settings_language() },
      { id: "time-zone", label: () => m.settings_time_zone() },
    ],
    Component: () => <General account={account} />,
  },
  {
    id: "appearance",
    order: 20,
    group: "app",
    label: () => m.settings_appearance(),
    icon: iconOf("appearance"),
    description: () => m.settings_appearance_description(),
    sections: [
      { id: "theme", label: () => m.settings_theme() },
      ...(appearanceRows?.sections ?? []),
    ],
    Component: () => <Appearance Rows={appearanceRows?.Rows} />,
  },
  {
    id: "input",
    order: 30,
    group: "app",
    label: () => m.settings_input(),
    icon: iconOf("keyboard"),
    description: () => m.settings_input_description(),
    sections: [{ id: "send-key", label: () => m.settings_send_key() }],
    Component: Input,
  },
];
